import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useAlert } from "../../Hooks/useAlert";
import billLanguage from "../../language/billLanguage";
import reportLanguage from "../../language/reportLanguage";
import { authAPI, billingAPI, customerAssignmentAPI, reportAPI } from "../../services/api";
import Alert from "../common/Alert";
import Spinner from "../common/Spinner";

function Bill() {
  const { language } = useLanguage();
  const reportText = reportLanguage[language];
  const text = billLanguage[language];
  const { showAlert, alertConfig } = useAlert();

  const location = useLocation();
  const isReportTabActive = location.pathname === "/report";
  const isBillTabActive = location.pathname === "/bill";

  // Get previous month name
  const getPreviousMonthName = () => {
    const currentDate = new Date();
    const previousMonth =
      currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    return reportText.months[previousMonth];
  };

  const previousMonthName = getPreviousMonthName();

  const [search, setSearch] = useState("");
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Filter state
  const [filterMode, setFilterMode] = useState(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterRef = useRef(null);

  // Popup state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [popupMonthOffset, setPopupMonthOffset] = useState(0);
  const [billHistory, setBillHistory] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [fourMonthTotals, setFourMonthTotals] = useState([]);
  const [fourMonthTotalsLoading, setFourMonthTotalsLoading] = useState(false);

  // Settle bill state
  const [settleAmount, setSettleAmount] = useState(0);
  const [settleFullPayment, setSettleFullPayment] = useState(false);
  const [settleSaving, setSettleSaving] = useState(false);

  useEffect(() => {
    loadBills();
  }, [activeTab, fromDate, toDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };
    if (showFilterMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterMenu]);

  const loadBills = async () => {
    try {
      setLoading(true);

      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const quarter = Math.ceil(month / 3);

      let response;

      if (activeTab === "month") {
        response = await reportAPI.getCustomerBills(
          "month",
          year,
          month,
          null,
          null,
          null,
        );
      } else if (activeTab === "quarter") {
        response = await reportAPI.getCustomerBills(
          "quarter",
          year,
          null,
          quarter,
          null,
          null,
        );
      } else if (activeTab === "custom" && fromDate && toDate) {
        response = await reportAPI.getCustomerBills(
          "custom",
          null,
          null,
          null,
          fromDate,
          toDate,
        );
      }
      if (response && response.success) {
        let data = response.data;
        const currentUser = authAPI.getCurrentUser();
        if (currentUser?.role === "Employee" && currentUser?.employeeId) {
          const empResp = await customerAssignmentAPI.getMyCustomers(currentUser.employeeId);
          if (empResp.success && Array.isArray(empResp.data)) {
            const assignedIds = new Set(empResp.data.map(c => c.customerId));
            data = data.filter(r => assignedIds.has(r.customerId));
          }
        }
        const billData = data.map((b) => ({
          id: b.customerId,
          name: b.customerName,
          currentBill: b.currentBill,
          pending: b.pendingAmount,
          totalDue: b.totalDue,
          currentBillWithAllowance: b.currentBillWithAllowance,
        }));

        setBills(billData);
        const paymentsInit = {};
        billData.forEach((c) => {
          paymentsInit[c.id] = {
            fullPayment: false,
            amount: 0,
            totalDue: c.totalDue,
          };
        });
        setPayments(paymentsInit);
        return billData;
      }
    } catch (error) {
      console.error("Error loading bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    let result = bills.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()),
    );

    if (filterMode === "paid") {
      result = result.filter((c) => c.totalDue === 0);
    } else if (filterMode === "unpaid") {
      result = result.filter((c) => c.totalDue > 0);
    } else if (filterMode === "pending") {
      result = result.filter((c) => c.pending > 0 && c.totalDue > 0);
    }

    if (filterMode) {
      result = [...result].sort((a, b) => b.totalDue - a.totalDue);
    }

    return result;
  }, [search, bills, filterMode]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleFullPaymentChange = (id, checked) => {
    setPayments((prev) => {
      const customerState = prev[id];
      const newAmount = checked ? customerState.totalDue : 0;
      return {
        ...prev,
        [id]: {
          ...customerState,
          fullPayment: checked,
          amount: newAmount,
        },
      };
    });
  };

  const handleAmountChange = (id, value) => {
    const numeric = Number(value) || 0;
    setPayments((prev) => {
      const customerState = prev[id];
      const totalDue = customerState.totalDue;

      // Validate: Don't allow amount greater than totalDue
      let finalAmount = numeric;
      if (numeric > totalDue) {
        finalAmount = totalDue;
      }

      // Auto-check fullPayment if amount equals totalDue
      const isFull = finalAmount >= totalDue && totalDue > 0;

      return {
        ...prev,
        [id]: {
          ...customerState,
          amount: finalAmount,
          fullPayment: isFull,
        },
      };
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      let periodStartDate, periodEndDate, billingPeriod;

      if (activeTab === "month") {
        periodStartDate = new Date(year, month - 1, 1)
          .toISOString()
          .split("T")[0];
        periodEndDate = new Date(year, month, 0).toISOString().split("T")[0];
        billingPeriod = "Month";
      } else if (activeTab === "quarter") {
        const quarter = Math.ceil(month / 3);
        const startMonth = (quarter - 1) * 3;
        periodStartDate = new Date(year, startMonth, 1)
          .toISOString()
          .split("T")[0];
        periodEndDate = new Date(year, startMonth + 3, 0)
          .toISOString()
          .split("T")[0];
        billingPeriod = "Quarter";
      } else {
        periodStartDate = fromDate;
        periodEndDate = toDate;
        billingPeriod = "Custom";
      }

      const customersToSave = filteredCustomers.filter(
        (c) => payments[c.id].amount > 0,
      );

      if (customersToSave.length === 0) {
        await showAlert({
          type: "warning",
          title: "No Payments",
          message: "Please enter payment amounts for at least one customer.",
        });
        return;
      }

      const paymentPromises = customersToSave.map((c) => {
        const p = payments[c.id];
        return billingAPI
          .saveBillPayment({
            customerId: c.id,
            currentBill: c.currentBillWithAllowance.toFixed(0),
            previousPending: c.pending,
            amountPaid: p.amount,
            billingPeriod: billingPeriod,
            periodStartDate: periodStartDate,
            periodEndDate: periodEndDate,
          })
          .then((response) => ({ customer: c.name, success: true, response }))
          .catch((error) => ({ customer: c.name, success: false, error }));
      });

      const results = await Promise.all(paymentPromises);

      const succeeded = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);

      if (failed.length === 0) {
        await showAlert({
          type: "success",
          title: "Success",
          message: `Bill payments saved successfully for ${succeeded.length} customer(s)!`,
        });
      } else if (succeeded.length > 0) {
        await showAlert({
          type: "warning",
          title: "Partial Success",
          message: `Saved ${succeeded.length} payment(s), but ${failed.length} failed. Failed customers: ${failed.map((f) => f.customer).join(", ")}`,
        });
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: `Failed to save all payments. Please try again.`,
        });
      }

      loadBills(); // Reload to get updated data
    } catch (error) {
      console.error("Error saving bill payments:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Error saving bill payments. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Bill History Popup Helpers ---
  const getPopupMonthYear = useCallback((offset) => {
    const d = new Date();
    d.setDate(1); // prevent day-overflow when current day > last day of target month
    d.setMonth(d.getMonth() + offset);
    return { year: d.getFullYear(), month: d.getMonth() + 1, date: d };
  }, []);

  const getMonthLabel = (offset) => {
    const { date } = getPopupMonthYear(offset);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  const loadBillHistory = useCallback(
    async (customerId, offset) => {
      setPopupLoading(true);
      setBillHistory([]);
      try {
        const { year, month } = getPopupMonthYear(offset);
        const startDate = new Date(year, month - 1, 1)
          .toISOString()
          .split("T")[0];
        const endDate = new Date(year, month, 0).toISOString().split("T")[0];
        const response = await billingAPI.getBillHistory(
          customerId,
          startDate,
          endDate,
        );
        if (response && response.success) {
          setBillHistory(response.data || []);
        } else {
          setBillHistory([]);
        }
      } catch (error) {
        console.error("Error loading bill history:", error);
        setBillHistory([]);
      } finally {
        setPopupLoading(false);
      }
    },
    [getPopupMonthYear],
  );

  const loadFourMonthTotals = useCallback(async (customerId) => {
    setFourMonthTotalsLoading(true);
    try {
      const response = await billingAPI.getCustomer4MonthBillTotals(customerId);
      if (response && response.success) {
        setFourMonthTotals(response.data || []);
      }
    } catch (error) {
      console.error("Error loading 4-month totals:", error);
      setFourMonthTotals([]);
    } finally {
      setFourMonthTotalsLoading(false);
    }
  }, []);

  const handleOpenPopup = (customer) => {
    setSelectedCustomer(customer);
    setPopupMonthOffset(0);
    setSettleAmount(0);
    setSettleFullPayment(false);
    loadBillHistory(customer.id, 0);
    loadFourMonthTotals(customer.id);
  };

  const handleClosePopup = () => {
    setSelectedCustomer(null);
    setBillHistory([]);
    setFourMonthTotals([]);
    setPopupMonthOffset(0);
  };

  const handlePopupMonthChange = (offset) => {
    setPopupMonthOffset(offset);
    loadBillHistory(selectedCustomer.id, offset);
  };

  const handlePopupSave = async () => {
    const c = selectedCustomer;
    if (!c) return;

    if (settleAmount <= 0) {
      await showAlert({
        type: "warning",
        title: "No Amount",
        message: "Please enter an amount to pay.",
      });
      return;
    }

    if (settleAmount > c.totalDue) {
      await showAlert({
        type: "warning",
        title: "Invalid Amount",
        message: "Amount cannot exceed total due of ₹" + c.totalDue.toFixed(0) + ".",
      });
      return;
    }

    try {
      setSettleSaving(true);

      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const periodStartDate = new Date(year, month - 1, 1).toISOString().split("T")[0];
      const periodEndDate = new Date(year, month, 0).toISOString().split("T")[0];

      const response = await billingAPI.saveBillPayment({
        customerId: c.id,
        currentBill: c.currentBillWithAllowance.toFixed(0),
        previousPending: c.pending,
        amountPaid: settleAmount,
        billingPeriod: "Month",
        periodStartDate: periodStartDate,
        periodEndDate: periodEndDate,
      });

      if (response && response.success) {
        await showAlert({
          type: "success",
          title: "Success",
          message: "Bill payment saved successfully for " + c.name + "!",
        });

        setSettleAmount(0);
        setSettleFullPayment(false);
        loadBillHistory(c.id, popupMonthOffset);
        loadFourMonthTotals(c.id);

        const billData = await loadBills();
        if (billData) {
          const updated = billData.find(b => b.id === c.id);
          if (updated) {
            setSelectedCustomer({ ...c, ...updated });
          }
        }
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: response?.message || "Failed to save payment.",
        });
      }
    } catch (error) {
      console.error("Error saving payment in popup:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Error saving payment. Please try again.",
      });
    } finally {
      setSettleSaving(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 bg-background-light p-4 pb-2 dark:bg-background-dark md:static md:p-6 md:pb-3">
        <div className="flex items-center justify-between md:hidden">
          <div className="flex items-center gap-2 ">
            <img className="logo" src="/images/logo.png" alt={text.appTitle} />
          </div>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">
            {text.pageTitle}
          </h1>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-primary rounded-lg text-white font-bold text-sm md:hidden disabled:opacity-50"
          >
            <i className="fas fa-save" />
            <span>{loading ? "Saving..." : text.save}</span>
          </button>
        </div>

        <div className="flex gap-2 mt-0 mb-2">
          <Link
            to="/report"
            className={`flex-1 h-10 flex items-center justify-center rounded-lg text-sm ${
              isReportTabActive
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
          >
            {reportText.tabReport} ({previousMonthName})
          </Link>
          <Link
            to="/bill"
            className={`flex-1 h-10 flex items-center justify-center rounded-lg text-sm ${
              isBillTabActive
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
          >
            {reportText.tabBill} ({previousMonthName})
          </Link>
        </div>

        <div className="mt-2 md:mt-0 flex items-center gap-3 relative">
          <div className="relative flex-1">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-0 focus:border-primary"
              placeholder={text.searchCustomerPlaceholder}
              type="text"
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <button
            onClick={() => setShowFilterMenu((p) => !p)}
            className={`flex items-center justify-center w-12 h-12 rounded-xl ${
              filterMode
                ? "bg-primary text-white"
                : "bg-primary/20 text-primary"
            }`}
          >
            <i className="fas fa-filter text-lg" />
          </button>

          <button
            onClick={handleSave}
            disabled={loading}
            className="hidden md:flex items-center justify-center gap-1 px-3 py-2 bg-primary rounded-lg text-white font-bold text-sm disabled:opacity-50"
          >
            <i className="fas fa-save" />
            <span>{loading ? "Saving..." : text.save}</span>
          </button>

          {/* Filter Dropdown */}
          {showFilterMenu && (
            <div
              ref={filterRef}
              className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
            >
              <div className="p-1">
                {[
                  { key: "paid", label: text.billPaid, icon: "fa-check-circle" },
                  { key: "unpaid", label: text.billUnpaid, icon: "fa-times-circle" },
                  { key: "pending", label: text.unpaidWithPending, icon: "fa-exclamation-circle" },
                  { key: null, label: text.clearFilter, icon: "fa-undo", divider: true },
                ].map((item) => (
                  <div key={item.key || "clear"}>
                    {item.divider && (
                      <hr className="mx-2 my-1 border-slate-200 dark:border-slate-700" />
                    )}
                    <button
                      onClick={() => {
                        setFilterMode(item.key);
                        setShowFilterMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                        filterMode === item.key
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                    >
                      <i className={`fas ${item.icon} w-5 text-center`} />
                      <span>{item.label}</span>
                      {filterMode === item.key && (
                        <i className="fas fa-check ml-auto text-primary" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pt-0 pb-24 md:p-6 md:pt-0 md:pb-6">
        {loading ? (
          <Spinner />
        ) : (
          <section>
            <div className="grid gap-3 md:grid-cols-1 lg:grid-cols-2">
              {filteredCustomers.map((c) => {
                const state = payments[c.id] || {
                  fullPayment: false,
                  amount: 0,
                  totalDue: 0,
                };
                const totalDue = state.totalDue;
                return (
                  <div
                    key={c.id}
                    className="customer-card bg-white dark:bg-slate-800/60 rounded-xl shadow-md p-2 md:p-3 cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleOpenPopup(c)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-bold text-slate-900 dark:text-white text-sm md:text-base">
                        {c.name}
                      </p>
                      <span className="text-xs md:text-sm bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">
                        ₹{totalDue.toFixed(0)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                      <span className="text-slate-600 dark:text-slate-400">
                        {text.currentBill}:{" "}
                        <span className="font-semibold text-slate-900 dark:text-white">
                          ₹{c.currentBillWithAllowance.toFixed(0)}
                        </span>
                      </span>
                      <span className="text-slate-600 dark:text-slate-400">
                        {text.pending}:{" "}
                        <span
                          className={`font-semibold ${
                            c.pending > 0 ? "text-red-600" : "text-green-600"
                          }`}
                        >
                          ₹{c.pending.toFixed(0)}
                        </span>
                      </span>
                      <label
                        className="inline-flex items-center gap-1 text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 dark:border-slate-500 w-3 h-3"
                          checked={state.fullPayment}
                          onChange={(e) =>
                            handleFullPaymentChange(c.id, e.target.checked)
                          }
                        />
                        <span>{text.fullPayment}</span>
                      </label>
                    </div>

                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {text.amountToPay}:
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={totalDue}
                        className="flex-1 h-8 md:h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                        value={state.amount || ""}
                        onChange={(e) =>
                          handleAmountChange(c.id, e.target.value)
                        }
                        placeholder="0"
                        title={`Maximum amount: ₹${totalDue.toFixed(2)}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Customer Details + Settlement Popup */}
      {selectedCustomer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleClosePopup}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-primary rounded-t-2xl">
              <h2 className="text-lg font-bold text-white truncate">
                {selectedCustomer.name}
              </h2>
              <button
                onClick={handleClosePopup}
                className="text-white hover:text-slate-200 transition ml-3 flex-shrink-0"
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">

              {/* ========== Section 1: Settle Bill ========== */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="fas fa-credit-card text-primary" />
                  {text.settleBill}
                </h3>

                {/* Bill Summary Cards */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-primary/5 dark:bg-primary/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{text.currentBill}</p>
                    <p className="text-lg font-bold text-primary">₹{selectedCustomer.currentBillWithAllowance.toFixed(0)}</p>
                  </div>
                  <div className="bg-primary/5 dark:bg-primary/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{text.pending}</p>
                    <p className="text-lg font-bold" style={selectedCustomer.pending > 0 ? {color: "#dc2626"} : {color: "#16a34a"}}>₹{selectedCustomer.pending.toFixed(0)}</p>
                  </div>
                  <div className="bg-primary/5 dark:bg-primary/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{text.totalDue}</p>
                    <p className="text-lg font-bold text-primary">₹{selectedCustomer.totalDue.toFixed(0)}</p>
                  </div>
                </div>

                {/* 4-Month Bill Summary Cards */}
                {!fourMonthTotalsLoading && fourMonthTotals.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{text.previousMonths}:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {fourMonthTotals.map((item, idx) => {
                        const monthName = new Date(item.year, item.month - 1).toLocaleString("en-US", { month: "short" });
                        const isLatest = idx === 0;
                        return (
                          <div
                            key={idx}
                            className={`rounded-lg p-2 text-center ${
                              isLatest
                                ? "bg-primary text-white"
                                : "bg-primary/10 text-primary dark:bg-primary/20"
                            }`}
                          >
                            <p className="text-xs font-semibold opacity-80">{monthName}</p>
                            <p className="text-base font-bold mt-0.5">₹{Number(item.currentBill).toFixed(0)}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Full Payment Checkbox */}
                <label className="inline-flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 dark:border-slate-500 w-4 h-4"
                    checked={settleFullPayment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSettleFullPayment(checked);
                      setSettleAmount(checked ? selectedCustomer.totalDue : 0);
                    }}
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{text.fullPayment}</span>
                </label>

                {/* Amount Input */}
                <div className="flex items-center gap-3 mb-3">
                  <label className="text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap font-medium">{text.amount}:</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedCustomer.totalDue}
                    className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    value={settleAmount || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value) || 0;
                      const clamped = Math.min(val, selectedCustomer.totalDue);
                      setSettleAmount(clamped);
                      setSettleFullPayment(clamped >= selectedCustomer.totalDue && selectedCustomer.totalDue > 0);
                    }}
                    placeholder={text.enterAmount}
                  />
                  <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">{text.max}: ₹{selectedCustomer.totalDue.toFixed(0)}</span>
                </div>

                {/* Save Button */}
                <button
                  onClick={handlePopupSave}
                  disabled={settleSaving || settleAmount <= 0}
                  className="w-full py-2.5 bg-primary hover:bg-[#007aa3] text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {settleSaving ? (
                    <span>Saving...</span>
                  ) : (
                    <><i className="fas fa-save" />{text.saveBill}</>
                  )}
                </button>
              </div>

              {/* Divider */}
              <hr className="my-2 border-slate-200 dark:border-slate-700" />

              {/* ========== Section 2: Bill History ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="fas fa-history text-primary" />
                  {text.billHistory}
                </h3>

                {/* Month Selector Buttons */}
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={() => handlePopupMonthChange(0)}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                      popupMonthOffset === 0
                        ? "bg-primary text-white"
                        : "bg-primary/20 text-primary dark:bg-primary/30"
                    }`}
                  >
                    {text.currentMonth}
                    <span className="block text-xs font-normal opacity-80">
                      {getMonthLabel(0)}
                    </span>
                  </button>
                  <button
                    onClick={() => handlePopupMonthChange(-1)}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                      popupMonthOffset === -1
                        ? "bg-primary text-white"
                        : "bg-primary/20 text-primary dark:bg-primary/30"
                    }`}
                  >
                    {text.previousMonth}
                    <span className="block text-xs font-normal opacity-80">
                      {getMonthLabel(-1)}
                    </span>
                  </button>
                </div>

                {/* Table */}
                {popupLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : billHistory.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                    <i className="fas fa-calendar-times text-4xl mb-3 block" />
                    <p className="text-sm">{text.noHistory}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                      {text.billingPeriod}: {billHistory[0]?.billingPeriod}{" "}
                      {billHistory[0]?.periodStartDate && (
                        <span className="text-slate-500 dark:text-slate-500">
                          ({new Date(billHistory[0].periodStartDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} - {new Date(billHistory[0].periodEndDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })})
                        </span>
                      )}
                    </div>
                    <table className="w-full text-sm border-collapse border border-slate-200 dark:border-slate-700">
                    <thead>
                      <tr className="text-sm uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/80">
                        <th className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                          {text.paidDate}
                        </th>
                        <th className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                          {text.currentBill} (₹)
                        </th>
                        <th className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                          {text.previousPending} (₹)
                        </th>
                        <th className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                          {text.amountPaid} (₹)
                        </th>
                        <th className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                          {text.totalDue} (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {billHistory.map((bill, idx) => {
                        const paidDate = new Date(
                          bill.paidDate,
                        ).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        });
                        return (
                          <tr
                            key={idx}
                            className="hover:bg-slate-50 dark:hover:bg-slate-700/40"
                          >
                            <td className="py-2.5 px-2 text-center text-xs text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                              {paidDate}
                            </td>
                            <td className="py-2.5 px-2 text-center font-semibold border border-slate-200 dark:border-slate-700">
                              {bill.currentBill.toFixed(0)}
                            </td>
                            <td className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                              <span
                                className={
                                  bill.previousPending > 0
                                    ? "text-red-600"
                                    : "text-slate-700 dark:text-slate-300"
                                }
                              >
                                {bill.previousPending.toFixed(0)}
                              </span>
                            </td>
                            <td className="py-2.5 px-2 text-center border border-slate-200 dark:border-slate-700">
                              <span className="text-green-600 font-semibold">
                                {bill.amountPaid.toFixed(0)}
                              </span>
                            </td>
                            <td className="py-2.5 px-2 text-center font-bold text-primary border border-slate-200 dark:border-slate-700">
                              {bill.newPending.toFixed(0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
            </div>

            {/* Popup Footer */}
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {billHistory.length} {text.entries}
              </span>
              <button
                onClick={handleClosePopup}
                className="px-5 py-2 bg-primary hover:bg-[#007aa3] text-white rounded-lg text-sm font-semibold transition"
              >
                {text.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Dialog */}
      {alertConfig && <Alert {...alertConfig} />}
    </>
  );
}

export default Bill;
